import type {
  Action,
  ActionGoBack,
  ActionGoNext,
  ActionSubmitAndGoNext
} from '@epilot/journey-logic-commons'
import { appendStepBlockId, clsx } from '@epilot/journey-logic-commons'
import { useEffect, useRef, useState } from 'react'

import { Button, Icon, CircularProgress, useTheme } from '../../components'
import { useLoadingListener } from '../../utils'

import classes from './ActionBarControl.module.scss'
import { useResize } from './useResize'

type ActionButtonsProps = {
  ctaButton?: ActionGoNext | ActionSubmitAndGoNext
  goBackButton?: ActionGoBack
  id: string
  onAction: (action: Action) => void
}

export const ActionButtons = ({
  ctaButton,
  goBackButton,
  id,
  onAction
}: ActionButtonsProps) => {
  const loading = useLoadingListener()

  const [isNavigatingDisabled, setIsNavigatingDisabled] = useState(true)

  // used to position ctaButton depending on containers width.
  // I.e. will be fullWidth by default in the sidebar
  const ref = useRef<null | HTMLDivElement>(null)
  const { width: containerWidth } = useResize(ref)
  const { breakpoints } = useTheme()
  const isMobile = containerWidth < breakpoints.sm

  useEffect(() => {
    setTimeout(() => {
      setIsNavigatingDisabled(false)
    }, 200)
  }, [])

  return (
    <div className={classes.container} ref={ref}>
      {containerWidth > 0 && (
        <div
          className={clsx(
            classes.innerContainer,
            !goBackButton?.isVisible
              ? classes.flexEnd
              : classes.flexSpaceBetween,
            isMobile ? classes.columnReverse : classes.row
          )}
        >
          {goBackButton?.isVisible && (
            <Button
              className={clsx(
                'js-next',
                classes.button,
                classes.backButton,
                isMobile && classes.fullWidth
              )}
              id={appendStepBlockId(id, 'Go Back Button')}
              isDisabled={loading}
              label={goBackButton.label}
              leftIcon={<Icon name="arrow_back" />}
              onClick={() => {
                if (!isNavigatingDisabled) {
                  onAction(goBackButton)
                }
              }}
            />
          )}

          {ctaButton?.isVisible && (
            <Button
              className={clsx(
                'js-next',
                classes.ctaButton,
                classes.button,
                isMobile && classes.fullWidth
              )}
              id={appendStepBlockId(id, 'CTA Button')}
              isDisabled={loading}
              label={ctaButton.label}
              onClick={() => {
                if (!isNavigatingDisabled) {
                  onAction(ctaButton)
                }
              }}
              rightIcon={
                loading ? <CircularProgress color="inherit" size={20} /> : null
              }
              variant="primary"
            />
          )}
        </div>
      )}
    </div>
  )
}
